/** A move as the apply sends it — only what batching reads. */
export interface BatchableMove { pronumber: number; street_turn_export?: number }

/** Moves per request. In region a PRO costs ~25 ms to snapshot and well under a second
 * to save (prod shadow timings, 2026-10-29), so 25 is a few seconds a request: the bar
 * moves often, the gateway timeout is nowhere near, and the per-request overhead stays
 * small next to one request for the whole board. */
export const APPLY_BATCH_SIZE = 25;

/**
 * Split a plan's moves into the requests that apply them (planner feedback, 2026-10-29).
 *
 * ONE REQUEST FOR THE WHOLE BOARD MEANT NO PROGRESS, AND NO WRITES FOR MINUTES. The
 * server snapshots every PRO in a request before it saves the first one, so a 766-move
 * apply against a local backend sat for 8½ minutes with nothing written and nothing on
 * screen, then saved for another 25. In batches, work lands as it goes and the screen
 * can say how far along it is from moves actually applied.
 *
 * MOVES THAT SHARE A PRO TRAVEL TOGETHER, a street turn's export included. A blocked
 * PRO is held back whole and a turn is refused when either of its PROs is blocked, and
 * the server decides both per request — a PRO split across two requests could let one
 * half through. A group larger than the batch size goes as one request.
 *
 * Board order is kept, so the board fills in driver by driver.
 */
export function applyBatches<M extends BatchableMove>(moves: M[], size = APPLY_BATCH_SIZE): M[][] {
  const parent = new Map<number, number>();
  const find = (pro: number): number => {
    if (!parent.has(pro)) parent.set(pro, pro);
    let root = pro;
    while (parent.get(root) !== root) root = parent.get(root)!;
    parent.set(pro, root);
    return root;
  };
  for (const move of moves) {
    if (move.street_turn_export) parent.set(find(move.street_turn_export), find(move.pronumber));
    else find(move.pronumber);
  }

  // Groups in order of their first move on the board.
  const groups = new Map<number, M[]>();
  for (const move of moves) {
    const root = find(move.pronumber);
    groups.set(root, [...(groups.get(root) || []), move]);
  }

  const batches: M[][] = [];
  let current: M[] = [];
  for (const group of groups.values()) {
    if (current.length && current.length + group.length > size) {
      batches.push(current);
      current = [];
    }
    current = [...current, ...group];
  }
  if (current.length) batches.push(current);
  return batches;
}

export interface ApplyProgress { done: number; total: number; startedAt: number }

export const progressPercent = (p: ApplyProgress) => (p.total ? Math.round((p.done / p.total) * 100) : 0);

/** "about 38 min left", from the rate the requests so far have actually run at. Null
 * until one has finished — a guess from nothing is worse than none — and once all are. */
export function timeLeft(p: ApplyProgress, now = Date.now()): string | null {
  if (p.done <= 0 || p.done >= p.total) return null;
  const msLeft = ((now - p.startedAt) / p.done) * (p.total - p.done);
  if (msLeft < 60_000) return 'under a minute left';
  const minutes = Math.round(msLeft / 60_000);
  if (minutes < 60) return `about ${minutes} min left`;
  return `about ${Math.floor(minutes / 60)} hr ${minutes % 60} min left`;
}
